import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import SearchableSelect from '@/components/common/SearchableSelect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Building2, Loader2, Save, CheckCircle, MapPin, FileText, Landmark, Palette, ImageIcon, Languages } from 'lucide-react';

interface OrgSettings {
  id: string;
  name: string;
  display_name: string;
}

export default function OrgSettingsPage() {
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [saved, setSaved] = useState<string | null>(null);

  // Bank account fields (from AdvisorProfile)
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [swiftBic, setSwiftBic] = useState('');
  const [iban, setIban] = useState('');

  // Branding
  const [brandColor, setBrandColor] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Language & Region
  const [clientFacingLanguage, setClientFacingLanguage] = useState(() => localStorage.getItem('client_facing_language') || 'en');
  const [dateFormat, setDateFormat] = useState(() => localStorage.getItem('date_format') || 'DD.MM.YYYY');
  const [defaultCurrency, setDefaultCurrency] = useState(() => localStorage.getItem('default_currency') || 'EUR');

  const { data: profile } = useQuery({
    queryKey: ['advisor-profile-mine'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      try {
        const res = await api.get('/advisor/profile/mine/');
        return res.data;
      } catch (e: any) {
        if (e.response?.status === 404) return null;
        throw e;
      }
    },
  });

  useEffect(() => {
    if (profile) {
      setBankAccountNumber(profile.bank_account_number || '');
      setSwiftBic(profile.swift_bic || '');
      setIban(profile.iban || '');
      setBrandColor(profile.brand_color || '');
      if (profile.logo_url) setLogoUrl(profile.logo_url);
    }
  }, [profile]);

  const uploadLogoMutation = useMutation({
    mutationFn: async (file: File) => {
      const api = await authenticatedBitswanBackendInstance();
      const formData = new FormData();
      formData.append('image', file);
      const uploadRes = await api.post('/advisor/content-images/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Save to profile
      if (profile?.id) {
        await api.patch(`/advisor/profile/${profile.id}/`, { logo: uploadRes.data.id });
      }
      return uploadRes.data;
    },
    onSuccess: (data) => {
      setLogoUrl(data.url);
      queryClient.invalidateQueries({ queryKey: ['advisor-profile-mine'] });
      setSaved('branding');
      setTimeout(() => setSaved(null), 3000);
    },
  });

  const brandMutation = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      if (profile?.id) {
        return api.patch(`/advisor/profile/${profile.id}/`, { brand_color: brandColor });
      }
      return api.post('/advisor/profile/', { brand_color: brandColor });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advisor-profile-mine'] });
      setSaved('branding');
      setTimeout(() => setSaved(null), 3000);
    },
  });

  const bankMutation = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const payload = {
        bank_account_number: bankAccountNumber,
        swift_bic: swiftBic,
        iban,
      };
      if (profile?.id) {
        return api.patch(`/advisor/profile/${profile.id}/`, payload);
      }
      return api.post('/advisor/profile/', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advisor-profile-mine'] });
      setSaved('bank');
      setTimeout(() => setSaved(null), 3000);
    },
  });

  const { data: org, isLoading } = useQuery({
    queryKey: ['advisor-org-settings'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/advisor/organizations/settings/');
      return res.data as OrgSettings;
    },
  });

  useEffect(() => {
    if (org) {
      if (!newName) setNewName(org.display_name);
    }
  }, [org]);

  const renameMutation = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.patch('/advisor/organizations/settings/', { name: newName });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['advisor-org-settings'] });
      localStorage.setItem('active_org_name', res.data.name);
      setSaved('name');
      setTimeout(() => setSaved(null), 3000);
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Organization Settings</h2>
        <p className="text-muted-foreground">Manage your advisor organization</p>
      </div>

      {/* Organization Name */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-5 w-5 text-amber-500" />
            Organization Name
          </CardTitle>
          <CardDescription>This name appears in the sidebar and on invoices</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Organization Name</label>
            <Input
              value={newName}
              onChange={(e) => { setNewName(e.target.value); setSaved(null); }}
              placeholder="Your organization name"
              data-testid="org-name-input"
              className="mt-1"
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => renameMutation.mutate()}
              disabled={!newName.trim() || renameMutation.isPending || newName === org?.display_name}
              data-testid="save-org-name"
            >
              {renameMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
            {saved === 'name' && (
              <span className="flex items-center gap-1 text-sm text-green-600" data-testid="org-name-saved">
                <CheckCircle className="h-4 w-4" /> Saved
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="billing-moved">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-5 w-5 text-amber-500" />
            Billing address and tax details
          </CardTitle>
          <CardDescription>
            These now live with your invoices, so what you enter is exactly what
            gets printed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline" data-testid="go-to-billing">
            <Link to="/billing">
              <FileText className="h-4 w-4 mr-2" />
              Open billing
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Bank Account */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Landmark className="h-5 w-5 text-amber-500" />
            Bank Account
          </CardTitle>
          <CardDescription>Bank details used for receiving payments on invoices</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <label className="text-xs text-muted-foreground">Account number (domestic)</label>
              <Input
                value={bankAccountNumber}
                onChange={(e) => setBankAccountNumber(e.target.value)}
                placeholder="19-2000145399/0800"
                data-testid="bank-account-number"
                className="mt-1"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">Czech format: prefix-number/bank code.</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">IBAN</label>
              <Input
                value={iban}
                onChange={(e) => setIban(e.target.value.toUpperCase().replace(/\s+/g, ''))}
                placeholder="CZ6508000000192000145399"
                data-testid="bank-iban"
                className="mt-1"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">For payments from abroad.</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">SWIFT / BIC</label>
              <Input
                value={swiftBic}
                onChange={(e) => setSwiftBic(e.target.value)}
                placeholder="GIBACZPX"
                data-testid="swift-bic"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button
          onClick={() => bankMutation.mutate()}
          disabled={bankMutation.isPending}
          data-testid="save-bank"
        >
          {bankMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Bank & Rate Info
        </Button>
        {saved === 'bank' && (
          <span className="flex items-center gap-1 text-sm text-green-600" data-testid="bank-saved">
            <CheckCircle className="h-4 w-4" /> Saved
          </span>
        )}
      </div>

      {/* Branding */}
      <Card data-testid="branding-section">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Palette className="h-5 w-5 text-amber-500" />
            Branding
          </CardTitle>
          <CardDescription>Logo and colors for client-facing pages (magic links, reports, proposals)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4">
            <div>
              <label className="text-sm font-medium">Logo</label>
              <div
                className="mt-1 w-20 h-20 rounded-xl border-2 border-dashed border-muted-foreground/20 hover:border-amber-500/40 flex items-center justify-center cursor-pointer transition-colors overflow-hidden bg-muted/20"
                onClick={() => logoInputRef.current?.click()}
                data-testid="branding-logo-upload"
              >
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
                ) : uploadLogoMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-muted-foreground/30" />
                )}
              </div>
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogoMutation.mutate(f); }} />
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <label className="text-sm font-medium">Primary Brand Color</label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="color"
                    value={brandColor || '#f59e0b'}
                    onChange={(e) => setBrandColor(e.target.value)}
                    className="h-10 w-10 rounded border cursor-pointer"
                    data-testid="brand-color-picker"
                  />
                  <Input
                    value={brandColor}
                    onChange={(e) => setBrandColor(e.target.value)}
                    placeholder="#f59e0b"
                    className="w-32"
                    data-testid="brand-color-input"
                  />
                  <div className="flex items-center gap-1">
                    {['#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4'].map(c => (
                      <button
                        key={c}
                        className="h-8 w-8 rounded-full border-2 transition-all"
                        style={{ backgroundColor: c, borderColor: brandColor === c ? c : 'transparent' }}
                        onClick={() => setBrandColor(c)}
                      />
                    ))}
                  </div>
                </div>
              </div>
              {/* Preview */}
              {brandColor && (
                <div className="p-3 rounded-lg border" style={{ borderColor: brandColor + '40', backgroundColor: brandColor + '08' }}>
                  <p className="text-xs" style={{ color: brandColor }}>Preview: This is how your brand color will look on client-facing pages.</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Branding */}
      <div className="flex items-center gap-3">
        <Button onClick={() => brandMutation.mutate()} disabled={brandMutation.isPending} data-testid="save-branding">
          {brandMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Branding
        </Button>
        {saved === 'branding' && (
          <span className="flex items-center gap-1 text-sm text-green-600">
            <CheckCircle className="h-4 w-4" /> Saved
          </span>
        )}
      </div>

      {/* Language & Region */}
      <Card data-testid="language-region-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5 text-blue-500" />
            Language & Region
          </CardTitle>
          <CardDescription>
            Configure language preferences and regional formatting for your portal and client-facing pages.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Client-Facing Language</label>
              <p className="text-xs text-muted-foreground mb-1">Language used in magic links, reports, and proposals sent to clients</p>
              <SearchableSelect
                value={clientFacingLanguage}
                onChange={setClientFacingLanguage}
                placeholder="Language"
                searchPlaceholder="Search languages…"
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'cs', label: 'Czech (Čeština)' },
                  { value: 'de', label: 'German (Deutsch)' },
                  { value: 'sk', label: 'Slovak (Slovenčina)' },
                  { value: 'pl', label: 'Polish (Polski)' },
                ]}
                testId="client-facing-language"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Default Currency</label>
              <p className="text-xs text-muted-foreground mb-1">Currency used for new invoices, proposals, and pricing</p>
              <SearchableSelect
                value={defaultCurrency}
                onChange={setDefaultCurrency}
                placeholder="Currency"
                searchPlaceholder="Search currencies…"
                options={[
                  { value: 'EUR', label: 'EUR (€)' },
                  { value: 'USD', label: 'USD ($)' },
                  { value: 'CZK', label: 'CZK (Kč)' },
                  { value: 'GBP', label: 'GBP (£)' },
                  { value: 'CHF', label: 'CHF' },
                  { value: 'PLN', label: 'PLN (zł)' },
                ]}
                testId="default-currency"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Date Format</label>
            <p className="text-xs text-muted-foreground mb-1">How dates are displayed across the portal and in client documents</p>
            <SearchableSelect
              className="max-w-xs"
              value={dateFormat}
              onChange={setDateFormat}
              placeholder="Date format"
              searchPlaceholder="Search formats…"
              options={[
                { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (31.12.2026)' },
                { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2026)' },
                { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-12-31)' },
                { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2026)' },
              ]}
              testId="date-format"
            />
          </div>
          <Button
            onClick={() => {
              localStorage.setItem('client_facing_language', clientFacingLanguage);
              localStorage.setItem('date_format', dateFormat);
              localStorage.setItem('default_currency', defaultCurrency);
              setSaved('region');
              setTimeout(() => setSaved(null), 2500);
            }}
            data-testid="save-region"
          >
            <Save className="h-4 w-4 mr-2" /> Save Region Settings
          </Button>
          {saved === 'region' && (
            <span className="flex items-center gap-1 text-sm text-green-600">
              <CheckCircle className="h-4 w-4" /> Saved
            </span>
          )}
        </CardContent>
      </Card>

      {org && (
        <div className="text-xs text-muted-foreground">
          Organization ID: {org.id}
        </div>
      )}
    </div>
  );
}
